import type { Build } from '@todesktop/shared';
import { Text, useStdin } from 'ink';
import isCi from 'is-ci';
import { useEffect, useState } from 'react';
import { isBuildCancellable } from '../utilities/buildStatus';
import { toError } from '../utilities/CliError';
import getCallableFirebaseFunction from '../utilities/getCallableFirebaseFunction';
import logForCI from '../utilities/logForCI';
import logger from '../utilities/logger';
import useExit from '../utilities/useExit';
import ErrorDisplay from './ErrorDisplay';

const callCancelBuildCloudFn = getCallableFirebaseFunction('cancelBuild');

export default function CancelBuild({
  buildId,
  cancellation,
  commandUsed,
}: {
  buildId: string;
  cancellation: Cancellation;
  commandUsed?: string;
}) {
  const { error, errorType, status } = cancellation;

  switch (status) {
    case 'cancel-failed': {
      if (!error) {
        return (
          <ErrorDisplay
            commandUsed={commandUsed}
            error="Sorry, something went wrong while cancelling the build."
          />
        );
      }

      if (errorType === 'internal') {
        return <ErrorDisplay commandUsed={commandUsed} error={error} />;
      }

      return (
        <Text>
          <Text color="red">Cannot cancel build ({buildId}).</Text>{' '}
          {error.message}
        </Text>
      );
    }

    case 'cancelled': {
      return <Text>Build cancelled</Text>;
    }

    case 'cancelling': {
      return <Text>Cancelling build ({buildId})...</Text>;
    }

    case 'idle':
    default: {
      return null;
    }
  }
}

export function useCancellation({
  abortController,
  appId,
  autoExit = true,
  build,
}: {
  abortController?: AbortController;
  appId?: string;
  autoExit?: boolean;
  build?: Build;
}): Cancellation {
  const { isRawModeSupported } = useStdin();
  const exit = useExit();
  const [state, setState] = useState<CancellationState>({ status: 'idle' });

  useEffect(() => {
    if (autoExit && state.status === 'cancelled' && !state.error) {
      logger.debug('useCancellation: exiting now that build is cancelled');
      setTimeout(exit, 10);
    }
  }, [autoExit, exit, state.status, state.error]);

  const canCancel = Boolean(
    appId &&
    !isCi &&
    isRawModeSupported &&
    state.status === 'idle' &&
    isBuildCancellable(build),
  );

  return {
    canCancel,
    cancel,
    error: state.error,
    errorType: state.errorType,
    executed: state.status !== 'idle',
    status: state.status,
  };

  async function cancel() {
    logger.debug('useCancellation: cancel() called');

    const buildId = build?.id;
    if (!appId || !buildId) {
      logger.warn("useCancellation: can't run with:", { appId, buildId });
      return;
    }

    abortController?.abort(new CanceledError('Build was cancelled by user'));
    setState({ status: 'cancelling' });
    logForCI('Cancelling build...');

    try {
      const result = await callCancelBuildCloudFn({ appId, buildId });
      logger.debug(
        { appId, buildId, result },
        'useCancellation: cancellation request complete',
      );
      setState({ status: 'cancelled' });
    } catch (e) {
      const error = toError<{ code?: string }>(e);

      logger.debug(
        { appId, buildId, code: error.code },
        'useCancellation: cancellation request failed',
      );
      logger.error(e);

      if (error.code === 'internal') {
        setState({
          status: 'cancel-failed',
          error: new Error(
            `Failed to cancel build (${buildId}). ${error.message}`,
          ),
          errorType: 'internal',
        });
      } else {
        setState({
          status: 'cancel-failed',
          error,
          errorType: 'other',
        });
      }
    }
  }
}

export class CanceledError extends Error {
  static isCanceled(e: unknown): e is Error {
    return (
      e instanceof CanceledError ||
      (e instanceof Error && e.name === 'CanceledError') // Axios CanceledError
    );
  }
}

export interface Cancellation {
  canCancel: boolean;
  cancel: () => Promise<void>;
  error?: Error;
  errorType?: 'internal' | 'other';
  executed?: boolean;
  status: CancellationStatus;
}

export type CancellationStatus =
  | 'idle'
  | 'cancelling'
  | 'cancelled'
  | 'cancel-failed';

interface CancellationState {
  status: CancellationStatus;
  error?: Error;
  errorType?: 'internal' | 'other';
}
